/**
 * Theme Selector Utility
 * Automatically selects the appropriate theme based on user email domain
 */

import theme, { darkTheme } from '../theme/theme';
import { generateThemeFromTenantConfig } from './themeGenerator';

// Store for dynamic tenant theme (set by TenantContext)
let cachedTenantTheme = null;
let cachedTenantConfig = null;

/**
 * Set the tenant theme configuration (called by TenantContext)
 * @param {Object} tenantConfig - Tenant theme configuration from API
 */
export const setTenantThemeConfig = (tenantConfig) => {
  if (tenantConfig && tenantConfig.primaryColor) {
    cachedTenantConfig = tenantConfig;
    cachedTenantTheme = generateThemeFromTenantConfig(tenantConfig);
  }
};

/**
 * Get the cached tenant theme
 * @returns {Object|null} Tenant theme or null
 */
export const getTenantTheme = () => cachedTenantTheme;

/**
 * Get theme based on user preference or tenant config
 * @param {string} email - User's email address (unused, kept for backwards compatibility)
 * @param {string} preferredTheme - Optional user preference override
 * @param {Object} tenantConfig - Optional tenant configuration for 'tenant' theme
 * @returns {Object} Material-UI theme object
 */
export const getThemeForUser = (email, preferredTheme = null, tenantConfig = null) => {
  // If user has a preference, use that
  if (preferredTheme) {
    return getThemeByName(preferredTheme, tenantConfig);
  }

  // If tenant theme is configured and cached, use that as default
  if (cachedTenantTheme) {
    return cachedTenantTheme;
  }

  // Default to standard theme
  return theme;
};

/**
 * Get theme by name
 * @param {string} themeName - Name of the theme
 * @param {Object} tenantConfig - Optional tenant configuration for 'tenant' theme
 * @returns {Object} Material-UI theme object
 */
export const getThemeByName = (themeName, tenantConfig = null) => {
  // Handle dynamic tenant theme
  if (themeName === 'tenant') {
    if (tenantConfig) {
      return generateThemeFromTenantConfig(tenantConfig);
    }
    // Try cached tenant theme
    if (cachedTenantTheme) {
      return cachedTenantTheme;
    }
    // Fall back to default
    return theme;
  }

  // Base themes - only Default and Dark Mode
  // Tenant-specific themes (selectAfrica, africanAlliance, etc.) come from tenant config
  const themes = {
    default: theme,
    dark: darkTheme,
  };

  // If requesting a legacy theme name, try cached tenant theme first
  if (!themes[themeName] && cachedTenantTheme) {
    return cachedTenantTheme;
  }

  return themes[themeName] || theme;
};

/**
 * Get theme name from email (deprecated - now uses tenant config)
 * @param {string} email - User's email address
 * @returns {string} Theme name - returns 'tenant' if tenant theme is cached, otherwise 'default'
 */
export const getThemeNameFromEmail = (email) => {
  // Theme is now determined by tenant config, not email domain
  if (cachedTenantTheme) {
    return 'tenant';
  }
  return 'default';
};

/**
 * Get available themes list (base themes only)
 * Tenant-specific themes are added by TenantContext
 * @returns {Array} List of available themes with metadata
 */
export const getAvailableThemes = () => [
  {
    id: 'default',
    name: 'Default',
    description: 'Standard Material-UI blue theme',
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
  },
  {
    id: 'dark',
    name: 'Dark Mode',
    description: 'Dark theme for low-light environments',
    primaryColor: '#1976d2',
    secondaryColor: '#dc004e',
  },
];

/**
 * Save user theme preference to localStorage
 * @param {string} themeName - Theme name to save
 */
export const saveThemePreference = (themeName) => {
  try {
    localStorage.setItem('userThemePreference', themeName);
  } catch (error) {
    console.error('Failed to save theme preference:', error);
  }
};

/**
 * Load user theme preference from localStorage
 * @returns {string|null} Saved theme name or null
 */
export const loadThemePreference = () => {
  try {
    return localStorage.getItem('userThemePreference');
  } catch (error) {
    console.error('Failed to load theme preference:', error);
    return null;
  }
};

/**
 * Clear user theme preference
 */
export const clearThemePreference = () => {
  try {
    localStorage.removeItem('userThemePreference');
  } catch (error) {
    console.error('Failed to clear theme preference:', error);
  }
};

export default getThemeForUser;
